import React, { useState, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import Text from 'core-system/Text'
import FlexContainer from 'core-system/FlexContainer'
import PrimaryButton from 'core-system/Button/PrimaryButton'
import SecondaryButton from 'core-system/Button/SecondaryButton'
import IconButton from '@mui/material/IconButton'
import DeleteOutline from '@mui/icons-material/DeleteOutline'
import EditOutlined from '@mui/icons-material/EditOutlined'
import {
  AutoEnrollRule,
  AutoEnrollField,
  AutoEnrollOperator,
  ConditionJoin,
  formatAutoEnrollRuleConditions,
} from '../autoEnrollTypes'
import { SegmentListType } from 'features/Programs/Shared/types'
import EnrollmentTargetBlock from './EnrollmentTargetBlock'
import {
  RuleLogicEditor,
  firstFieldOtherThan,
  asStringArray,
  hasConditionValue,
  isListOperator,
} from 'features/RuleBuilder'

const CardRoot = styled.div<{ $editing?: boolean }>`
  border: 1px solid ${(p) => p.theme.palette.grey.grey300};
  border-radius: 0.75rem;
  padding: 1.25rem 1.35rem;
  background: ${(p) => p.theme.palette.white};
  transition:
    box-shadow 0.15s ease,
    border-color 0.15s ease;
  ${(p) =>
    p.$editing &&
    `
    border-color: ${p.theme.palette.grey.grey600};
    box-shadow: 0 0 0 3px ${p.theme.palette.grey.grey200};
  `}
`

const TopRow = styled(FlexContainer)`
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 0.75rem;
`

const Summary = styled(Text)`
  line-height: 1.45;
  max-width: 100%;
`

const MetaRow = styled(FlexContainer)`
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  margin-bottom: 0.65rem;
`

const Pill = styled.span<{ $tone: 'active' | 'inactive' | 'neutral' }>`
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  ${(p) =>
    p.$tone === 'active' &&
    `
    background: ${p.theme.palette.success.green100};
    color: ${p.theme.palette.success.green800};
  `}
  ${(p) =>
    p.$tone === 'inactive' &&
    `
    background: ${p.theme.palette.grey.grey200};
    color: ${p.theme.palette.grey.grey700};
  `}
  ${(p) =>
    p.$tone === 'neutral' &&
    `
    background: ${p.theme.palette.grey.grey100};
    color: ${p.theme.palette.grey.grey700};
  `}
`

const TargetRow = styled(FlexContainer)`
  margin-top: 0.15rem;
  flex-wrap: wrap;
  gap: 0.35rem;
  align-items: baseline;
`

const EditorStack = styled.div`
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid ${(p) => p.theme.palette.grey.grey300};
`

const EditorSurface = styled.div`
  border-radius: 0.5rem;
  padding: 1rem;
  background: ${(p) => p.theme.palette.grey.grey100};
  border: 1px solid ${(p) => p.theme.palette.grey.grey300};
  margin-bottom: 1rem;
`

const SectionEyebrow = styled(Text)`
  margin-bottom: 0.65rem;
`

const ActionsRow = styled(FlexContainer)`
  margin-top: 0.75rem;
  gap: 0.5rem;
  justify-content: flex-end;
`

export interface RuleCardProps {
  rule: AutoEnrollRule
  targetSummary: string
  groupPickerItems: SegmentListType[]
  tagPickerItems: SegmentListType[]
  /** False when employer has no custom groups (hide prune/active toggles). */
  showEnrollmentControls?: boolean
  onDelete: (id: string) => void
  onSave: (id: string, changes: Partial<Omit<AutoEnrollRule, 'id'>>) => void
}

const RuleCard: React.FC<RuleCardProps> = ({
  rule,
  targetSummary,
  groupPickerItems,
  tagPickerItems,
  showEnrollmentControls = true,
  onDelete,
  onSave,
}) => {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<AutoEnrollRule | null>(null)
  const [secondOn, setSecondOn] = useState(false)
  const [thirdOn, setThirdOn] = useState(false)

  const startEdit = () => {
    const hasSecond =
      Boolean(rule.secondaryField) &&
      hasConditionValue(
        rule.secondaryValue ?? '',
        rule.secondaryOperator ?? 'equals'
      )
    const hasThird =
      hasSecond &&
      Boolean(rule.tertiaryField) &&
      hasConditionValue(
        rule.tertiaryValue ?? '',
        rule.tertiaryOperator ?? 'equals'
      )
    setSecondOn(hasSecond)
    setThirdOn(hasThird)
    setDraft({
      ...rule,
      segments: [...(rule.segments ?? [])],
      tags: [...(rule.tags ?? [])],
      secondaryField: rule.secondaryField ?? 'additional_info',
      secondaryOperator: rule.secondaryOperator ?? 'equals',
      secondaryValue: rule.secondaryValue ?? '',
      conditionJoin: (rule.conditionJoin as ConditionJoin) ?? 'and',
      tertiaryField: rule.tertiaryField ?? 'title',
      tertiaryOperator: rule.tertiaryOperator ?? 'equals',
      tertiaryValue: rule.tertiaryValue ?? '',
      tertiaryConditionJoin:
        (rule.tertiaryConditionJoin as ConditionJoin) ?? 'and',
    })
    setEditing(true)
  }

  const cancel = () => {
    setEditing(false)
    setDraft(null)
    setSecondOn(false)
    setThirdOn(false)
  }

  const save = () => {
    if (
      !draft ||
      !hasConditionValue(draft.value, draft.operator) ||
      (!draft.segments?.length && !draft.tags?.length)
    ) {
      return
    }
    const secOp = draft.secondaryOperator ?? 'equals'
    const terOp = draft.tertiaryOperator ?? 'equals'
    if (secondOn && !hasConditionValue(draft.secondaryValue ?? '', secOp)) return
    if (thirdOn && !hasConditionValue(draft.tertiaryValue ?? '', terOp)) return
    const normalizedValue = isListOperator(draft.operator)
      ? asStringArray(draft.value)
      : String(draft.value).trim()
    const hasSecondary =
      secondOn &&
      hasConditionValue(draft.secondaryValue ?? '', secOp) &&
      draft.secondaryField
    const hasTertiary =
      thirdOn &&
      hasSecondary &&
      hasConditionValue(draft.tertiaryValue ?? '', terOp) &&
      draft.tertiaryField
    const secondaryPayload = hasSecondary
      ? {
          secondaryField: draft.secondaryField,
          secondaryOperator: secOp,
          secondaryValue: isListOperator(secOp)
            ? asStringArray(draft.secondaryValue ?? '')
            : String(draft.secondaryValue ?? '').trim(),
          conditionJoin: (draft.conditionJoin as ConditionJoin) ?? 'and',
        }
      : {
          secondaryField: null,
          secondaryOperator: null,
          secondaryValue: null,
          conditionJoin: null,
        }
    const tertiaryPayload = hasTertiary
      ? {
          tertiaryField: draft.tertiaryField,
          tertiaryOperator: terOp,
          tertiaryValue: isListOperator(terOp)
            ? asStringArray(draft.tertiaryValue ?? '')
            : String(draft.tertiaryValue ?? '').trim(),
          tertiaryConditionJoin:
            (draft.tertiaryConditionJoin as ConditionJoin) ?? 'and',
        }
      : {
          tertiaryField: null,
          tertiaryOperator: null,
          tertiaryValue: null,
          tertiaryConditionJoin: null,
        }
    onSave(draft.id, {
      field: draft.field,
      operator: draft.operator,
      value: normalizedValue,
      segments: draft.segments,
      tags: draft.tags ?? [],
      isActive: draft.isActive ?? true,
      removeNonMatchingMembers: draft.removeNonMatchingMembers ?? false,
      ...secondaryPayload,
      ...tertiaryPayload,
    })
    cancel()
  }

  const setSecondConditionEnabled = (next: boolean) => {
    setSecondOn(next)
    if (!next) {
      setThirdOn(false)
      setDraft((d) =>
        d ? { ...d, secondaryValue: '', tertiaryValue: '' } : d
      )
    }
  }

  const setThirdConditionEnabled = (next: boolean) => {
    setThirdOn(next)
    if (!next) {
      setDraft((d) => (d ? { ...d, tertiaryValue: '' } : d))
    }
  }

  useEffect(() => {
    if (!editing || !draft || !secondOn) return
    if (draft.secondaryField === draft.field) {
      setDraft((d) =>
        d
          ? {
              ...d,
              secondaryField: firstFieldOtherThan(
                d.field,
                ...(thirdOn && d.tertiaryField ? [d.tertiaryField] : [])
              ),
            }
          : d
      )
    }
  }, [editing, secondOn, thirdOn, draft?.field, draft?.secondaryField, draft?.tertiaryField])

  useEffect(() => {
    if (!editing || !draft || !thirdOn) return
    if (
      draft.tertiaryField === draft.field ||
      draft.tertiaryField === draft.secondaryField
    ) {
      setDraft((d) =>
        d
          ? {
              ...d,
              tertiaryField: firstFieldOtherThan(d.field, d.secondaryField ?? 'additional_info'),
            }
          : d
      )
    }
  }, [
    editing,
    thirdOn,
    draft?.field,
    draft?.secondaryField,
    draft?.tertiaryField,
  ])

  const draftActiveGroupItems = useMemo((): SegmentListType[] => {
    if (!draft?.segments?.length) return []
    return draft.segments
      .map((id) => groupPickerItems.find((p) => p.id === id))
      .filter((x): x is SegmentListType => Boolean(x))
  }, [draft?.segments, groupPickerItems])

  const draftActiveTagItems = useMemo((): SegmentListType[] => {
    if (!draft?.tags?.length) return []
    return draft.tags
      .map((id) => tagPickerItems.find((p) => p.id === id))
      .filter((x): x is SegmentListType => Boolean(x))
  }, [draft?.tags, tagPickerItems])

  return (
    <CardRoot $editing={editing}>
      <TopRow flexDirection='row'>
        <div style={{ minWidth: 0, flex: 1 }}>
          <Text
            variant='caption'
            color='grey600'
            fontWeight={600}
            marginBottom='0.35rem'
            style={{ letterSpacing: '0.06em' }}
          >
            WHEN MATCHED
          </Text>
          {editing && draft ? (
            <Summary variant='body2' color='grey800'>
              Editing rule…
            </Summary>
          ) : (
            <Summary variant='body1' color='grey800' fontWeight={500}>
              {formatAutoEnrollRuleConditions(rule)}
            </Summary>
          )}
        </div>
        <FlexContainer style={{ gap: '0.15rem', flexShrink: 0 }}>
          {editing ? null : (
            <IconButton
              size='small'
              onClick={startEdit}
              aria-label='Edit rule'
              sx={{ padding: '0.35rem' }}
            >
              <EditOutlined fontSize='small' />
            </IconButton>
          )}
          <IconButton
            size='small'
            onClick={() => onDelete(rule.id)}
            aria-label='Delete rule'
            sx={{ padding: '0.35rem' }}
          >
            <DeleteOutline fontSize='small' />
          </IconButton>
        </FlexContainer>
      </TopRow>

      {!editing && (
        <>
          <MetaRow flexDirection='row'>
            <Pill $tone={rule.isActive !== false ? 'active' : 'inactive'}>
              {rule.isActive !== false ? 'Active' : 'Paused'}
            </Pill>
            <Pill $tone={rule.removeNonMatchingMembers ? 'neutral' : 'neutral'}>
              {rule.removeNonMatchingMembers
                ? 'Prune non-matching'
                : 'Add-only'}
            </Pill>
          </MetaRow>
          <TargetRow flexDirection='row'>
            <Text variant='body2' color='grey800' style={{ fontWeight: 600 }}>
              Then enroll in
            </Text>
            <Text variant='body2' color='grey600'>
              {targetSummary}
            </Text>
          </TargetRow>
        </>
      )}

      {editing && draft && (
        <EditorStack>
          <EditorSurface>
            <SectionEyebrow variant='body2' color='grey600' fontWeight={600}>
              Conditions
            </SectionEyebrow>
            <RuleLogicEditor
              density='compact'
              field={draft.field}
              operator={draft.operator}
              value={draft.value}
              onFieldChange={(f) =>
                setDraft((d) => (d ? { ...d, field: f } : d))
              }
              onOperatorChange={(o) =>
                setDraft((d) => (d ? { ...d, operator: o } : d))
              }
              onValueChange={(v) =>
                setDraft((d) => (d ? { ...d, value: v } : d))
              }
              addSecondCondition={secondOn}
              onAddSecondConditionChange={setSecondConditionEnabled}
              conditionJoin={(draft.conditionJoin as ConditionJoin) ?? 'and'}
              onConditionJoinChange={(j) =>
                setDraft((d) => (d ? { ...d, conditionJoin: j } : d))
              }
              secondaryField={
                (draft.secondaryField ?? 'additional_info') as AutoEnrollField
              }
              secondaryOperator={
                (draft.secondaryOperator ?? 'equals') as AutoEnrollOperator
              }
              secondaryValue={draft.secondaryValue ?? ''}
              onSecondaryFieldChange={(f) =>
                setDraft((d) => (d ? { ...d, secondaryField: f } : d))
              }
              onSecondaryOperatorChange={(o) =>
                setDraft((d) => (d ? { ...d, secondaryOperator: o } : d))
              }
              onSecondaryValueChange={(v) =>
                setDraft((d) => (d ? { ...d, secondaryValue: v } : d))
              }
              addThirdCondition={thirdOn}
              onAddThirdConditionChange={setThirdConditionEnabled}
              tertiaryConditionJoin={
                (draft.tertiaryConditionJoin as ConditionJoin) ?? 'and'
              }
              onTertiaryConditionJoinChange={(j) =>
                setDraft((d) => (d ? { ...d, tertiaryConditionJoin: j } : d))
              }
              tertiaryField={
                (draft.tertiaryField ?? 'title') as AutoEnrollField
              }
              tertiaryOperator={
                (draft.tertiaryOperator ?? 'equals') as AutoEnrollOperator
              }
              tertiaryValue={draft.tertiaryValue ?? ''}
              onTertiaryFieldChange={(f) =>
                setDraft((d) => (d ? { ...d, tertiaryField: f } : d))
              }
              onTertiaryOperatorChange={(o) =>
                setDraft((d) => (d ? { ...d, tertiaryOperator: o } : d))
              }
              onTertiaryValueChange={(v) =>
                setDraft((d) => (d ? { ...d, tertiaryValue: v } : d))
              }
            />
          </EditorSurface>
          <EnrollmentTargetBlock
            groupItems={groupPickerItems}
            activeGroupItems={draftActiveGroupItems}
            onActiveGroupItemsChange={(items) =>
              setDraft((d) =>
                d ? { ...d, segments: items.map((i) => i.id) } : d
              )
            }
            tagItems={tagPickerItems}
            activeTagItems={draftActiveTagItems}
            onActiveTagItemsChange={(items) =>
              setDraft((d) =>
                d ? { ...d, tags: items.map((i) => i.id) } : d
              )
            }
            removeNonMatchingMembers={draft.removeNonMatchingMembers ?? false}
            onRemoveNonMatchingChange={(next) =>
              setDraft((d) =>
                d ? { ...d, removeNonMatchingMembers: next } : d
              )
            }
            isActive={draft.isActive ?? true}
            onActiveChange={(next) =>
              setDraft((d) => (d ? { ...d, isActive: next } : d))
            }
            showGroupControls={showEnrollmentControls}
          />
          <ActionsRow flexDirection='row'>
            <SecondaryButton onClick={cancel}>Cancel</SecondaryButton>
            <PrimaryButton onClick={save}>Save changes</PrimaryButton>
          </ActionsRow>
        </EditorStack>
      )}
    </CardRoot>
  )
}

export default React.memo(RuleCard)
